import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import api from "../../lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Badge } from "@/components/ui/Badge";
import { Textarea } from "@/components/ui/Textarea";
import { ChevronLeft, Search } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Eye, EyeOff, ArrowLeft, UserPlus, X } from "lucide-react";
import toast from "react-hot-toast";
import { validateEmail } from "../../lib/utils";

const CreateClientPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    commissioned_by: "",
    email: "",
    phone: "",
    contact_first_name: "",
    contact_last_name: "",
    is_active: true,
    notes: "",
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createMutation = useMutation((clientData) => api.post("/clients", clientData), {
    onSuccess: () => {
      toast.success("Client created successfully");
      queryClient.invalidateQueries(["clients"]);
      navigate("/clients");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to create client");
    },
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allFieldsEmpty =
      !formData.name.trim() &&
      !formData.email.trim() &&
      !formData.contact_first_name.trim() &&
      !formData.contact_last_name.trim();

    if (allFieldsEmpty) {
      toast.error("Please fill in the required data");
      return;
    }

    let hasErrors = false;

    if (!formData.name.trim()) {
      toast.error("Company name is required");
      hasErrors = true;
    }

    if (!formData.email.trim()) {
      toast.error("Email address is required");
      hasErrors = true;
    } else if (!validateEmail(formData.email)) {
      toast.error("Please enter a valid email address");
      hasErrors = true;
    }

    if (!formData.contact_first_name.trim()) {
      toast.error("Contact first name is required");
      hasErrors = true;
    }

    if (!formData.contact_last_name.trim()) {
      toast.error("Contact last name is required");
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    createMutation.mutate(formData);
  };

  return (
    <div className="">
      <div className="w-full">
        <div className="slide-up">
          <form onSubmit={handleSubmit} noValidate>
            <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="min-w-0 space-y-2">
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  className="w-full pl-3 text-xs font-medium text-gray-0"
                  onChange={handleChange}
                  placeholder="John"
                />
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="commissioned_by">Commissioned By</Label>
                <Input
                  id="commissioned_by"
                  name="commissioned_by"
                  value={formData.commissioned_by}
                  className="w-full pl-3 text-xs font-medium text-gray-0"
                  onChange={handleChange}
                  placeholder="Commissioned by"
                />
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="contact_first_name">Company Contact First Name *</Label>
                <Input
                  id="contact_first_name"
                  name="contact_first_name"
                  value={formData.contact_first_name}
                  className="w-full pl-3 text-xs font-medium text-gray-0"
                  onChange={handleChange}
                  placeholder="John"
                />
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="contact_last_name">Company Contact Last Name *</Label>
                <Input
                  id="contact_last_name"
                  name="contact_last_name"
                  value={formData.contact_last_name}
                  className="w-full pl-3 text-xs font-medium text-gray-0"
                  onChange={handleChange}
                  placeholder="doe"
                />
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="email">Company Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="text"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="w-full pl-3 text-xs font-medium text-gray-0"
                />
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="phone">Company phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="number"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="9879281010"
                  className="w-full pl-3 text-xs font-medium text-gray-0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="pl-3 text-xs font-medium text-gray-0"
              />
            </div>

            <div className="flex items-center py-6 space-x-2">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="rounded"
              />
              <Label htmlFor="is_active">Active Client</Label>
            </div>

            <div className="flex justify-end border-t border-b border-gray-0">
              <div className="flex items-center justify-center w-auto">
                <Link className="border-r rounded-b-none border-gray-0" to="/clients">
                  <Button
                    variant="secondary"
                    className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal uppercase rounded-none text-gray-0 hover:scale-100"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2 text-red-500" />
                    Back
                  </Button>
                </Link>
                <Button
                  className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal text-red-500 uppercase bg-transparent border-0 rounded-none rounded-b-none hover:scale-100"
                  type="submit"
                  disabled={createMutation.isLoading}
                >
                  {createMutation.isLoading ? "Creating..." : "Save"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
export default CreateClientPage;
